// Global Instructions Rule Applied!
import React, { useCallback, useMemo, useRef, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useCurrencies } from '../../../currencies/useCurrencies.ts'
import { useCommitDealGate } from '../../hooks/useCommitDealGate.tsx'
import { InputManager } from '../../../../core/components/InputManager/index.ts'
import { buildAccountRoleOptions } from '../../../../core/utils/accountRoleOptions.ts'
import {
  mapColumnToInputManagerField,
  handleAutoPopulation,
  reportingModuleKeyToTableInputManagerModule,
  type TableInputManagerModule
} from '../../../shared/table_input_manager_mappers/index.ts'
import { updateDealRecord } from '../../../../state_manager/state_update_actions/deals/index.ts'
import { updateCompanyRecord } from '../../../../state_manager/state_update_actions/companies/index.ts'
import { FieldTypeEnum } from '../../../../core/components/InputManager/types.ts'
import { DealFields } from '../../../../state_manager/data_models/deal_record.ts'
import type { ViewConfigColumn } from '../../../../core/components/ViewConfig/types.ts'
import { createTableColumns } from '../../../../ui_configs/table_grid_columns.ts'
import { VIEW_TASK_CONFIG } from '../../../../ui_configs/fund_management/view_task_config.ts'

import TableView from '../../../../core/components/TableView/TableView.tsx'
import type { FilterConfig } from '../../../../core/components/TableView/TableViewTypes.ts'

interface DealsTableProps {
  /** Column definitions from global configs (`reporting_column_defaults` + per-report `reporting_columns`). */
  gridColumns: ViewConfigColumn[]
  loading: boolean
  loadingMore: boolean
  hasMore: boolean
  currentPage: number
  activeFilters: any[]
  activeSorting: any
  filterOptions: any
  filterOptionsLoading: boolean
  onLoadMore: () => void
  onFiltersChange: (filters: FilterConfig) => void
  onSortChange: (sortConfig: any) => void
  onDealNameClick: (fieldKey: string, value: any, dealId: string) => void
  onTasksClick?: (tasks: any[], dealRecord: any, taskId: string | null) => void
  onTaskToggle?: (taskId: string, recordId: string, completed: boolean) => void
  getLookupLabel: (lookupId: string) => string
  formatAmountById: (amount: number | null, currencyId: string | null) => string
  getPipelineStageOptions: () => any[]
  getPipelineStageOptionsForDeal: (deal: any) => any[]
  getLookupOptions: (category: string) => any[]
}

/**
 * Deals table component with virtualized scrolling and editing capabilities
 */
const DealsTable: React.FC<DealsTableProps> = ({
  gridColumns,
  loading,
  loadingMore,
  hasMore,
  currentPage,
  activeFilters,
  activeSorting,
  filterOptions,
  filterOptionsLoading,
  onLoadMore,
  onFiltersChange,
  onSortChange,
  onDealNameClick,
  onTasksClick,
  onTaskToggle,
  getLookupLabel,
  formatAmountById,
  getPipelineStageOptions,
  getPipelineStageOptionsForDeal,
  getLookupOptions
}) => {
  // Redux dispatch
  const dispatch = useDispatch()

  // Get countries data from Global Configs Redux state
  const countriesData = useSelector((state: any) => state.globalConfigs?.countries || [])

  // Get pipeline configs and stages from Redux state for label lookup
  const pipelineConfigs = useSelector((state: any) => state.globalConfigs?.pipeline_configs || [])
  const pipelineStages = useSelector((state: any) => state.globalConfigs?.pipeline_stages || [])
  const accountRoleChoices = useSelector((state: any) => state.globalConfigs?.account_role_choices || [])
  const fundsData = useSelector((state: any) => state.globalConfigs?.funds || [])

  // Get updated deals data from Redux state
  const dealsData = useSelector((state: any) => state.deals?.deal?.data || [])

  // Currency functionality for number formatting
  const { getCurrencyById } = useCurrencies()

  // Commit gate for the Pipeline Stage cell (prompts when Committed is chosen with details missing)
  const { gate: commitGate, holder: commitGateHolder } = useCommitDealGate()

  const getRoleOptions = useCallback(() => buildAccountRoleOptions(accountRoleChoices), [accountRoleChoices])

  const getFundOptions = useCallback(() => {
    return fundsData.map((fund: { id: string; name?: string }) => ({
      value: fund.id,
      label: fund.name || ''
    }))
  }, [fundsData])

  // Helper function to extract text content from React elements
  const extractTextFromElement = useCallback((element: any): string => {
    if (typeof element === 'string') return element
    if (typeof element === 'number') return element.toString()
    if (!element) return ''

    if (React.isValidElement<{ children?: React.ReactNode }>(element)) {
      if (element.props?.children) {
        if (typeof element.props.children === 'string') {
          return element.props.children
        }
        if (Array.isArray(element.props.children)) {
          return element.props.children.map((child: any) => extractTextFromElement(child)).join('')
        }
        return extractTextFromElement(element.props.children)
      }
    }

    return ''
  }, [])

  /** Apply deal-grid denormalizations, sync related company store when editing account columns, then merge into the deal row. */
  const finalizeDealGridAfterPatch = useCallback(
    (record: any, inputManagerModule: TableInputManagerModule, updates: Record<string, any>) => {
      const u = { ...updates }
      if (DealFields.accounts__business_country_code in u) {
        const countryCode = u[DealFields.accounts__business_country_code]
        if (countryCode) {
          u[DealFields.accounts__business_country_label] = countriesData.find(
            (country: any) => country.code === countryCode
          )?.name
        } else {
          u[DealFields.accounts__business_country_label] = null
        }
      }

      if (u[DealFields.pipeline_deals__stage]) {
        const pipelineConfigId = u[DealFields.pipeline_deals__stage]
        u[DealFields.pipeline_config__id] = pipelineConfigId
        const config = pipelineConfigs.find((c: any) => c.id === pipelineConfigId)
        if (config) {
          const stage = pipelineStages.find((s: any) => s.id === config.stage)
          if (stage && stage.name) {
            u[DealFields.pipeline_deals__stage__label] = stage.name
          }
        }
      }

      if (inputManagerModule === 'companies') {
        const companyId = record.accounts__id || (record as Record<string, string | undefined>).deals__account
        if (companyId) {
          dispatch(updateCompanyRecord({ companyId: String(companyId), updates: { ...u } }))
        }
      }

      dispatch(
        updateDealRecord({
          dealId: record.deals__id,
          updates: u
        })
      )
    },
    [dispatch, countriesData, pipelineConfigs, pipelineStages]
  )

  // InputManager render function for cell editing
  const renderInputManagerColumn = useCallback(
    (
      record: any,
      columnKey: string,
      fieldId: string,
      fieldType: FieldTypeEnum,
      editingContext: any,
      columnInfo: any = {},
      action: any
    ) => {
      const isRequired = columnInfo.required || false
      const columnTitle = columnInfo.title || columnKey
      const isColumnEditable = columnInfo.editable !== false

      // Get updated record from Redux state instead of using static record
      const updatedRecord = dealsData?.find((deal: any) => deal.deals__id === record.deals__id) || record

      // Generate InputManager field configuration
      const reportingModuleKey = columnInfo.moduleKey as string | undefined
      const inputManagerModule: TableInputManagerModule = reportingModuleKeyToTableInputManagerModule(
        reportingModuleKey,
        'deals'
      )

      const dealsHelpers = {
        getLookupLabel,
        formatAmountById,
        getCurrencyById,
        getPipelineStageOptions,
        getPipelineStageOptionsForDeal,
        confirmStageChange: commitGate
      }
      const companiesHelpers = {
        getCurrencyById,
        getLookupOptions,
        getRoleOptions,
        getFundOptions
      }
      const contactsHelpers = {
        getLookupLabel,
        getLookupOptions
      }
      const fundraisingHelpers = {
        getLookupLabel,
        getLookupOptions,
        getCurrencyById
      }

      let helpers: Record<string, unknown> = dealsHelpers
      if (inputManagerModule === 'companies') {
        helpers = companiesHelpers
      } else if (inputManagerModule === 'contacts') {
        helpers = contactsHelpers
      } else if (inputManagerModule === 'fundraising') {
        helpers = fundraisingHelpers
      }

      const fieldConfig = {
        ...mapColumnToInputManagerField(
          inputManagerModule,
          { dataIndex: columnKey, title: columnTitle, required: isRequired, editable: isColumnEditable },
          updatedRecord,
          helpers,
          fieldType
        ),
        key: `${record.deals__id}-${columnKey}`
      }

      // Extract roundId for Round-related fields
      const roundId = updatedRecord[DealFields.rounds__id] || updatedRecord.round || null
      const isRoundField =
        columnKey === DealFields.prog__valuation ||
        columnKey === DealFields.rounds__funding_stage ||
        columnKey === DealFields.rounds__security_type ||
        columnKey === DealFields.rounds__raise_amount

      // Override editable property for Round fields when roundId is missing
      if (isRoundField) {
        fieldConfig.editable = fieldConfig.editable && !!roundId
      }

      // Handle field value changes
      const handleFieldChange = (fieldKey: string, newValue: any) => {
        // Handle auto-population for related fields
        handleAutoPopulation(
          inputManagerModule,
          fieldKey,
          newValue,
          record,
          (key: string, value: any) => {
            if (editingContext.onCellDataChange) {
              editingContext.onCellDataChange(value)
            }
          },
          helpers
        )

        // Update the cell data
        if (editingContext.onCellDataChange) {
          editingContext.onCellDataChange(newValue)
        }
      }

      // Handle field edit state changes for column width expansion
      const handleFieldEdit = (isEditing: boolean) => {
        if (editingContext?.onFieldEdit) {
          editingContext.onFieldEdit(columnKey, isEditing)
        }
      }

      // Handle field error
      const handleFieldError = (fieldKey: string, error: any) => {
        console.error(`Error in field ${fieldKey}:`, error)
      }

      // Alignment
      const getFieldAlignment = (): 'left' | 'right' => {
        if (
          fieldType === FieldTypeEnum.CURRENCY ||
          fieldType === FieldTypeEnum.NUMBER ||
          fieldType === FieldTypeEnum.VALUATION
        ) {
          return 'right'
        }
        return 'left'
      }

      // Apply truncation to all columns except tasks column
      const needsTruncation = columnKey !== DealFields.prog__tasks

      // Determine patchId - prefer mapper-provided patchId (e.g. pipeline_deals__id for pipeline stage),
      // then fall back to round/dependent row IDs.
      const patchId = (fieldConfig as any).patchId ?? (isRoundField && roundId ? roundId : record[fieldId])

      // Always return InputManager - it handles edit/display mode internally
      return (
        <div
          style={{
            width: '100%',
            ...(needsTruncation
              ? {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }
              : {})
          }}
        >
          <InputManager
            inputMode='read'
            labelPosition='none'
            alignment={getFieldAlignment()}
            fields={[fieldConfig]}
            onChange={handleFieldChange}
            onSave={() => {}} // Auto-Patch managed
            onError={handleFieldError}
            onPatchSuccess={(_passedRecordId, updates) =>
              finalizeDealGridAfterPatch(record, inputManagerModule, updates)
            }
            onFieldEdit={handleFieldEdit}
            enableAutoPatch={true}
            patchId={patchId}
            recordId={record.deals__id}
            className='cell-edit-input-manager'
            onClick={action}
            textClassName={needsTruncation ? 'truncate' : undefined}
          />
        </div>
      )
    },
    [
      getLookupLabel,
      formatAmountById,
      getCurrencyById,
      getPipelineStageOptions,
      getPipelineStageOptionsForDeal,
      getLookupOptions,
      getRoleOptions,
      getFundOptions,
      finalizeDealGridAfterPatch,
      dealsData,
      commitGate
    ]
  )

  // Create refs for callbacks to prevent unnecessary rerenders
  const handlePrimaryActionRef = useRef(onDealNameClick)
  const handleSecondaryActionRef = useRef(() => {})
  const handleTasksClickRef = useRef(onTasksClick)
  const handleTaskToggleRef = useRef(onTaskToggle)
  const renderInputManagerColumnRef = useRef(renderInputManagerColumn)

  // Update refs whenever callbacks change
  useEffect(() => {
    handlePrimaryActionRef.current = onDealNameClick
  }, [onDealNameClick])

  useEffect(() => {
    handleTasksClickRef.current = onTasksClick
  }, [onTasksClick])

  useEffect(() => {
    handleTaskToggleRef.current = onTaskToggle
  }, [onTaskToggle])

  useEffect(() => {
    renderInputManagerColumnRef.current = renderInputManagerColumn
  }, [renderInputManagerColumn])

  const { columns, frozenColumns } = useMemo(() => {
    return createTableColumns({
      columns: gridColumns,
      handlePrimaryActionRef,
      handleSecondaryActionRef,
      handleTasksClickRef,
      handleTaskToggleRef,
      renderInputManagerColumnRef,
      viewTaskConfig: VIEW_TASK_CONFIG.deals,
      fallbackRecordModuleKey: 'deals'
    })
  }, [gridColumns])

  // Deduplicate deals by deals__id to prevent duplicate React keys
  const dataSource = useMemo(() => {
    if (!dealsData || dealsData.length === 0) return []

    const seenIds = new Set<string>()
    const filtered = dealsData.filter((deal: any) => {
      const dealId = deal[DealFields.deals__id]
      if (!dealId || seenIds.has(dealId)) {
        return false
      }
      seenIds.add(dealId)
      return true
    })
    const mapped = filtered.map((deal: any) => ({
      ...deal,
      key: deal[DealFields.deals__id],
      id: deal[DealFields.deals__id]
    }))
    return mapped
  }, [dealsData])

  return (
    <div className='flex-1 min-h-0 h-full w-full'>
      {commitGateHolder}
      <TableView
        defaultSortColumn='pipeline_stage'
        columns={columns}
        dataSource={dataSource}
        onLoadMore={onLoadMore}
        loading={loading}
        loadingMore={loadingMore}
        hasMore={hasMore}
        currentPage={currentPage}
        enableCellEditing={true}
        currentFilters={(() => {
          return Array.isArray(activeFilters)
            ? activeFilters.reduce((acc: any, filter: any) => {
                acc[filter.key] = filter.value
                return acc
              }, {})
            : activeFilters || {}
        })()}
        onFiltersChange={onFiltersChange}
        onSortChange={onSortChange}
        filterOptions={filterOptions}
        filterOptionsLoading={filterOptionsLoading}
        currentSortConfig={
          Array.isArray(activeSorting) && activeSorting.length > 0
            ? { column: activeSorting[0].key, direction: activeSorting[0].direction }
            : null
        }
        rowHeight={40}
        pageSize={1000}
        frozenColumns={frozenColumns}
      />
    </div>
  )
}

export default DealsTable
